import {Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";

const MULTISELECT_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => LcCustomMultiselectV1Component),
	multi: true,
};

/**
 * Improved multi-selection version of the select, supporting value clearing.
 *
 * Clearing affordances (enabled via [clearable]):
 *  - trash icon shown inside the (closed) trigger when at least one value is selected
 *  - "Clear" button in the footer of the opened panel
 *  - the search bar "x" only clears the search text (not the selection)
 *
 * The cleared value is defined by [clearValue] (default: empty array).
 * Implements ControlValueAccessor so it can be used with [(ngModel)] (e.g. in filters).
 */
@Component({
	selector: 'lc-custom-multiselect-v1',
	templateUrl: './lc-custom-multiselect-v1.component.html',
	styleUrls: ['./lc-custom-multiselect-v1.component.scss'],
	providers: [MULTISELECT_VALUE_ACCESSOR]
})
export class LcCustomMultiselectV1Component implements OnChanges, ControlValueAccessor
{
	@Input()
	label: String | undefined;

	@Input()
	placeholder: String | undefined;

	@Input()
	options?: any[];

	@Input()
	value: any = [];

	@Input()
	disabled: boolean | undefined = false;

	@Output()
	onSelectionChange: EventEmitter<any> = new EventEmitter<any>();

	@Input()
	extraClass: string = '';

	@Input()
	searchable: boolean = false;

	/** Enables the clearing affordances (trigger trash icon + footer "Clear" button). */
	@Input()
	clearable: boolean = false;

	/** Value assigned/emitted when the selection is cleared. */
	@Input()
	clearValue: any = [];

	/** When true (default), the panel closes after clearing; when false it stays open. */
	@Input()
	closeOnClear: boolean = true;

	searchValue: string = ""; //The ID rendered are filtered by this value

	filterOptions: any[] | undefined; //Contains the filter options on selector

	@ViewChild('selectElement') selectElement: MatSelect | undefined;
	@ViewChild('searchInput') searchInput: ElementRef | undefined;
	@ViewChild('optionsScroll') optionsScroll: ElementRef | undefined;

	constructor()
	{
	}

	ngOnChanges(changes: SimpleChanges)
	{
		if (changes["options"]?.currentValue)
		{
			const current = changes["options"].currentValue;

			if (this.searchValue)
			{
				this.filterOptions = [...current].filter(option => this.matchesSearch(option, this.searchValue));
			}
			else
			{
				this.filterOptions = [...current];
			}
		}
	}

	oncSelectionChange(event: MatSelectChange): void
	{
		this.value = event.value;
		this.onChange(event.value);
		this.onSelectionChange.emit(event.value);
	}

	onSearchValueChange(event: any)
	{
		if (event)
		{
			this.filterOptions = this.options?.filter(option => this.matchesSearch(option, event));
		}
		else
		{
			this.searchValue = "";
			this.filterOptions = this.options;
		}
	}

	openedChange(event: any)
	{
		if (event)
		{
			//Reset the search on every open
			this.searchValue = "";
			this.filterOptions = this.options;

			//Scroll the options list (not the whole panel) to the top
			let scroller = this.optionsScroll?.nativeElement as HTMLDivElement;
			if (scroller)
			{
				scroller.scrollTop = 0;
			}

			//Give focus to the input so the user can search immediately
			this.searchInput?.nativeElement.focus();
		}
	}

	/** Clears the current selection (deselects all). */
	clear(event?: Event): void
	{
		if (event)
		{
			//Prevent the panel from toggling when clearing from the closed trigger
			event.stopPropagation();
			event.preventDefault();
		}

		this.value = this.clearValue;
		this.onChange(this.clearValue);
		this.onSelectionChange.emit(this.clearValue);

		this.searchValue = "";
		this.filterOptions = this.options;

		if (this.closeOnClear)
		{
			this.selectElement?.close();
		}
	}

	get hasValue(): boolean
	{
		return Array.isArray(this.value) ? this.value.length > 0 : !!this.value;
	}

	trackByOptionId(index: number, option: any): any
	{
		return option?.id ?? index;
	}

	private matchesSearch(option: any, search: string): boolean
	{
		const term = String(search ?? '').toUpperCase();
		const id = String(option?.id ?? '').toUpperCase();
		const name = String(option?.name ?? '').toUpperCase();
		return id.includes(term) || name.includes(term);
	}

	onChange: (value: any) => void = () => {};
	onTouched: () => void = () => {};

	writeValue(value: any): void
	{
		this.value = (value === undefined || value === null || value === '') ? [] : value;
	}

	registerOnChange(fn: (value: any) => void): void
	{
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void
	{
		this.onTouched = fn;
	}
}
